/**
 * Shared application states: a request failure, an authorization refusal, an expired session, a
 * missing MFA factor, an unprovisioned account and a wrong URL.
 *
 * Each one says what happened and what the operator can do next. None of them exposes a stack
 * trace, SQL, a provider message or a token; when the backend supplied a correlation id it is
 * shown, because that is the only thing support needs to find the request in the audit log.
 */
import { type ReactElement, useState } from 'react';
import { Link } from 'react-router-dom';
import { errorReference, isApiError, userMessage } from '../api/errors';
import { completeFirstOwnerSetup } from '../api/internal';
import { useSession } from '../auth/session';
import { Box, Button, Card, CardHead, CardPad, PageHead, Stack } from '../ui';
import { ROUTES } from './routes';

export function ErrorState({
  error,
  onRetry,
  title = 'İşlem tamamlanamadı',
}: {
  error: unknown;
  onRetry?: () => void;
  title?: string;
}): ReactElement {
  const reference = errorReference(error);
  return (
    <Card>
      <CardHead title={title} />
      <CardPad>
        <Stack>
          <Box tone="warn">
            <b>{userMessage(error)}</b>
            {isApiError(error) && error.details.length > 0 ? (
              <ul style={{ margin: '8px 0 0 18px', padding: 0 }}>
                {error.details.map((detail) => (
                  <li key={`${detail.key}-${detail.code}`}>{detail.message}</li>
                ))}
              </ul>
            ) : null}
          </Box>
          {reference ? (
            <span className="help">
              Destek başvurusu için işlem referansı: <span className="mono">{reference}</span>
            </span>
          ) : null}
          {onRetry ? (
            <div className="row-gap">
              <Button variant="secondary" onClick={onRetry}>
                Yeniden dene
              </Button>
            </div>
          ) : null}
        </Stack>
      </CardPad>
    </Card>
  );
}

/** Inline failure for a panel inside an otherwise working screen. */
export function InlineError({ error, onRetry }: { error: unknown; onRetry?: () => void }): ReactElement {
  return (
    <Box tone="warn">
      <div className="row-gap">
        <span>{userMessage(error)}</span>
        {onRetry ? (
          <Button size="sm" variant="ghost" onClick={onRetry} className="push-right">
            Yeniden dene
          </Button>
        ) : null}
      </div>
    </Box>
  );
}

export function Forbidden({ what }: { what?: string }): ReactElement {
  return (
    <Stack>
      <PageHead
        eyebrow="Yetki"
        title="Bu işlem için yetkiniz yok"
        lead={
          what
            ? `${what} için hesabınızın rolü yetkili değil.`
            : 'Hesabınızın rolü bu ekran için yetkili değil.'
        }
      />
      <Card>
        <CardPad>
          <Stack>
            <p className="small muted" style={{ margin: 0 }}>
              Yetki kararı sunucuda verilir. Rolünüzün değişmesi gerekiyorsa bir yönetici Kullanıcılar
              ekranından atama yapabilir.
            </p>
            <div className="row-gap">
              <Link className="btn btn-secondary" to={ROUTES.dashboard}>
                Panele dön
              </Link>
            </div>
          </Stack>
        </CardPad>
      </Card>
    </Stack>
  );
}

export function NotFound(): ReactElement {
  return (
    <Stack>
      <PageHead
        eyebrow="404"
        title="Sayfa bulunamadı"
        lead="Adres değişmiş veya kayıt kaldırılmış olabilir."
      />
      <Card>
        <CardPad>
          <Link className="btn btn-secondary" to={ROUTES.dashboard}>
            Panele dön
          </Link>
        </CardPad>
      </Card>
    </Stack>
  );
}

/** Shown when a request came back 401 while the app believed it was signed in. */
export function SessionExpired(): ReactElement {
  const { signOut } = useSession();
  return (
    <div className="login-wrap">
      <div className="login-card">
        <h1>Oturum süresi doldu</h1>
        <p className="small muted">
          Güvenlik nedeniyle oturumunuz kapatıldı. Devam etmek için yeniden giriş yapın; yarım kalan bir işlem
          varsa tekrar başlatmanız gerekir.
        </p>
        <Button
          variant="primary"
          block
          onClick={() => {
            void signOut();
          }}
        >
          Yeniden giriş yap
        </Button>
      </div>
    </div>
  );
}

/**
 * A verified session whose account this installation has never heard of.
 *
 * The person signed in with a password against an address the provider has confirmed; the server
 * simply has no internal user for that provider subject. On an installation whose first
 * administrator has not been created yet, that is the state EVERY account is in — including the one
 * the setup is waiting for.
 *
 * So this screen offers exactly one thing: finishing that setup, with the session already in the
 * browser (PO 7.10 §7). Nothing is copied, no console is opened and no shell is needed. Pressing
 * the button sends an empty request; the server decides from the session alone whether this is the
 * identity the deployment allowlisted, and refuses everything else with one answer.
 *
 * Which is also why the refusal here says so little. The server will not say whether the setup is
 * switched off, whether an owner already exists, or whether this is the wrong account — telling an
 * anonymous caller any of those is precisely what that uniform answer prevents — so this screen
 * relays what it was given and the correlation id that lets an operator look the rest up.
 *
 * Signing out stays available and stays secondary: somebody who signed in with the wrong account
 * needs a way back, and the session is otherwise the only thing the setup can use.
 */
export function UnknownUser(): ReactElement {
  const { signOut, auth, api, refreshActor } = useSession();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  async function completeSetup(): Promise<void> {
    setError(null);
    setBusy(true);
    try {
      await completeFirstOwnerSetup(api);
      /* The console opens because `/me` now resolves an owner — not because this screen decided
         it did. If the server is somehow still unconvinced, the same screen comes back. */
      await refreshActor();
    } catch (cause) {
      setError(cause);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="login-wrap">
      <div className="login-card">
        <h1>İlk yönetici kurulumu bekleniyor</h1>
        <p className="small muted">
          Giriş yapıldı, ancak {auth.email ? <span className="mono">{auth.email}</span> : 'bu hesap'} için
          uygulamada tanımlı bir kullanıcı ve rol yok.
        </p>
        <Box tone="info">
          Kurulum henüz tamamlanmadıysa aşağıdaki düğmeyle bu oturumu kullanarak ilk yöneticiyi
          tanımlayabilirsiniz. Sistem zaten kullanımdaysa veya bu hesap kurulum için tanımlı değilse istek
          reddedilir ve hiçbir şey yazılmaz; o durumda bir yöneticinin hesabınıza rol vermesi gerekir.
        </Box>
        {error ? (
          <Box tone="warn">
            <b>{userMessage(error)}</b>
            {errorReference(error) ? (
              <div className="small" style={{ marginTop: 6 }}>
                Destek başvurusu için işlem referansı: <span className="mono">{errorReference(error)}</span>
              </div>
            ) : null}
          </Box>
        ) : null}
        <Button
          variant="primary"
          block
          busy={busy}
          onClick={() => {
            void completeSetup();
          }}
        >
          İlk yönetici kurulumunu tamamla
        </Button>
        <p className="help">
          Oturumunuz açık tutuluyor; kurulum bu oturumu kullanır. Çıkış yaparsanız baştan giriş yapmanız
          gerekir.
        </p>
        <Button
          variant="ghost"
          block
          onClick={() => {
            void signOut();
          }}
        >
          Yine de çıkış yap
        </Button>
      </div>
    </div>
  );
}

/**
 * A provisioned account with no active role — which is the case where an administrator demonstrably
 * already exists, because somebody created this user.
 *
 * Fail-closed, and closed is all it is: nothing is offered, no command is named, and the session is
 * left alone rather than torn down. The backend authorizes every command against the matrix
 * regardless; this screen only stops the console being drawn around an actor with no role.
 */
export function NoRoleAssigned(): ReactElement {
  const { signOut, auth } = useSession();
  return (
    <div className="login-wrap">
      <div className="login-card">
        <h1>Yetkiniz tanımlı değil</h1>
        <p className="small muted">
          {auth.email ? <span className="mono">{auth.email}</span> : 'Bu hesap'} için etkin bir rol
          bulunamadı. Yönetim ekranları rol atanana kadar açılmaz.
        </p>
        <p className="help">
          Bir yöneticinin hesabınıza rol ataması gerekir. Oturumunuz açık kalır; rol atandıktan sonra sayfayı
          yenilemeniz yeterlidir.
        </p>
        <Button
          variant="ghost"
          block
          onClick={() => {
            void signOut();
          }}
        >
          Çıkış yap
        </Button>
      </div>
    </div>
  );
}

/** Configuration is missing (no API base URL, no Supabase project). */
export function NotConfigured({ detail }: { detail: string }): ReactElement {
  return (
    <div className="login-wrap">
      <div className="login-card">
        <h1>Uygulama yapılandırılmadı</h1>
        <p className="small muted">{detail}</p>
        <p className="help">
          Bu ekran yalnız ortam değişkenleri eksik olduğunda görünür; gizli anahtarlar tarayıcıya hiçbir zaman
          verilmez.
        </p>
      </div>
    </div>
  );
}
